import ArticlesAPI from '../../../api/article';
import {
  RESULTS_LIMIT_HEADER,
  RESULTS_LIMIT_PAGE,
  RESULTS_LIMIT_ENTER,
  TYPING_DEBOUNCE_MS,
  PAGINATION_SIZE,
  AI_SUMMARY_MAX_CHARS,
  DOM_IDS,
} from '../constants/searchConstants';

export default {
  data() {
    return {
      isEnter: false,
      searchTerm: '',
      isLoading: false,
      showSearchBox: false,
      searchResults: [],
      pageSearch: 0,
      pageTotal: 0,
      summary: '',
      searchResultsTotal: [],
      itemTotal: 0,
      isAutoSearch: false,
      feedbackSubmitted: null,
      showThankYou: false,
      hasSearchError: false,
      searchResultsContainer: null,
    };
  },

  computed: {
    portalSlug() {
      return window.portalConfig.portalSlug;
    },
    localeCode() {
      return window.portalConfig.localeCode;
    },
    shouldShowSearchBox() {
      return this.searchTerm !== '' && this.showSearchBox && !this.isEnter;
    },
    searchTranslations() {
      const { searchTranslations = {} } = window.portalConfig;
      return searchTranslations;
    },
    isHeaderMode() {
      return !window.location.pathname.match(/\/hc\/[^/]+\/[^/]+$/);
    },
    isSearchPage() {
      return window.location.pathname.includes('/search');
    },
  },

  watch: {
    searchTerm() {
      if (!this.isAutoSearch && !this.isSearchPage) {
        this.isEnter = false;
      }
      if (this.typingTimer) {
        clearTimeout(this.typingTimer);
      }
      const box = document.getElementById(DOM_IDS.boxCategory);
      if (box && box.classList.contains('hidden')) {
        box.classList.remove('hidden');
      }
      this.openSearch();
      this.isLoading = true;
      this.typingTimer = setTimeout(() => {
        this.fetchArticlesByQuery();
      }, TYPING_DEBOUNCE_MS);
    },
    currentPage() {
      this.clearSearchTerm();
    },
    isSearchPage: {
      immediate: true,
      handler(newValue) {
        if (newValue) {
          this.isEnter = true;
        }
      },
    },
  },

  async mounted() {
    this.searchResultsContainer = document.getElementById(
      DOM_IDS.searchResultsContainer
    );
    if (this.searchResultsContainer && !this.isSearchPage) {
      this.searchResultsContainer.style.display = 'none';
    }
    this.autoExtractQuery();
    if (this.isSearchPage) {
      this.isEnter = true;
    }
  },

  updated() {
    if (
      this.isEnter &&
      this.$refs.searchResults &&
      this.searchResultsContainer
    ) {
      const resultsElement = this.$refs.searchResults;
      if (resultsElement.parentElement !== this.searchResultsContainer) {
        this.searchResultsContainer.appendChild(resultsElement);
      }
      this.searchResultsContainer.style.display = '';
      this.togglePageContent(false);
      this.adjustGradientHeight(true);
    } else {
      if (this.searchResultsContainer) {
        this.searchResultsContainer.style.display = 'none';
      }
      this.togglePageContent(true);
      this.adjustGradientHeight(false);
    }
  },

  beforeDestroy() {
    clearTimeout(this.typingTimer);
  },

  methods: {
    adjustGradientHeight(reduceHeight) {
      const gradientSection = document.getElementById(DOM_IDS.portalBgGradient);
      if (!gradientSection) return;
      if (reduceHeight) {
        gradientSection.classList.remove('min-h-[400px]', 'md:min-h-[430px]');
        gradientSection.classList.add('min-h-[200px]', 'md:min-h-[220px]');
      } else {
        gradientSection.classList.remove('min-h-[200px]', 'md:min-h-[220px]');
        gradientSection.classList.add('min-h-[400px]', 'md:min-h-[430px]');
      }
    },

    togglePageContent(show) {
      // Elements with no visibility key in portals.content.visibility: always
      // follow search state directly.
      const unconditionalElements = [
        document.getElementById(DOM_IDS.heroTitle),
        document.querySelector('.article-page-background-section'),
      ];
      unconditionalElements.forEach(el => {
        if (el) el.style.display = show ? '' : 'none';
      });

      // Elements governed by portals.content.visibility: when entering search
      // (show === false) they're always hidden, since search results replace
      // homepage content regardless of config. When leaving/typing (show ===
      // true) they may only be restored if the admin config also allows it —
      // window.portalIsSectionVisible is the single source of truth, shared
      // with the initial server-rendered page load.
      const sectionVisibilityKeyByDomId = {
        [DOM_IDS.suggestionsWrapper]: 'heroSection',
        [DOM_IDS.categoriesSection]: 'categoryIcons',
        [DOM_IDS.featuredVideos]: 'featuredVideos',
        [DOM_IDS.servicesFeatures]: 'servicesFeatures',
        [DOM_IDS.mostConsulted]: 'mostConsultedTopics',
        [DOM_IDS.serviceInformation]: 'serviceInformation',
      };
      Object.entries(sectionVisibilityKeyByDomId).forEach(
        ([domId, visibilityKey]) => {
          const el = document.getElementById(domId);
          if (!el) return;
          el.style.display =
            show && window.portalIsSectionVisible(visibilityKey) ? '' : 'none';
        }
      );
    },

    pageAdd() {
      this.pageSearch += 1;
      this.searchResults = this.searchResultsTotal[this.pageSearch - 1];
    },

    pageRemove() {
      if (this.pageSearch <= 1) return;
      this.pageSearch -= 1;
      this.searchResults = this.searchResultsTotal[this.pageSearch - 1];
    },

    splitArray(array, chunkSize) {
      const result = [];
      for (let i = 0; i < array.length; i += chunkSize) {
        result.push(array.slice(i, i + chunkSize));
      }
      return result;
    },

    extractTextFromHTML(html) {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;
      return tempDiv.textContent || tempDiv.innerText || '';
    },

    showFragment(text, term) {
      const maxChar = 70;
      const index = text.toLowerCase().indexOf(term.toLowerCase());
      if (index === -1) {
        const fragment = text.substring(0, maxChar);
        const lastSpace = fragment.lastIndexOf(' ');
        return lastSpace === -1
          ? fragment + '...'
          : text.substring(0, lastSpace) + '...';
      }
      let start = Math.max(0, index - maxChar);
      let end = Math.min(text.length, index + term.length + maxChar);
      if (start > 0) {
        start = text.lastIndexOf(' ', start) + 1;
        if (start < 0) start = 0;
      }
      if (end < text.length) {
        const lastSpace = text.lastIndexOf(' ', end);
        end = lastSpace === -1 ? end : lastSpace;
      }
      const prefix = start > 0 ? '... ' : '';
      const suffix = end < text.length ? ' ...' : '';
      return prefix + text.substring(start, end) + suffix;
    },

    prepareContent(content) {
      return this.highlightContent(content, this.searchTerm, 'font-bold');
    },

    prepareContentFragment(content) {
      const text = this.extractTextFromHTML(content);
      const fragment = this.showFragment(text, this.searchTerm);
      return this.highlightContent(
        fragment,
        this.searchTerm,
        'font-bold bg-blue-100 text-blue-900 px-1 rounded'
      );
    },

    navigateToArticle(article) {
      window.location.href = article.link;
    },

    async handleEnter() {
      this.hideContent404();
      this.isEnter = true;
      this.isLoading = true;
      await this.fetchArticlesByQuery(RESULTS_LIMIT_ENTER, true);
      if (this.searchResults.length > PAGINATION_SIZE) {
        this.searchResultsTotal = this.splitArray(
          this.searchResults,
          PAGINATION_SIZE
        );
        this.searchResults = this.searchResultsTotal[0];
        this.pageSearch = 1;
        this.pageTotal = this.searchResultsTotal.length;
      }
      this.isLoading = false;
      [
        DOM_IDS.boxCategory,
        DOM_IDS.authSection,
        DOM_IDS.portalBgArticle,
      ].forEach(id => {
        const el = document.getElementById(id);
        if (el) el.classList.add('hidden');
      });
    },

    openSearch() {
      this.showSearchBox = true;
    },

    closeSearch() {
      if (!this.isSearchPage) {
        this.showSearchBox = false;
      }
    },

    clearSearchTerm() {
      this.searchTerm = '';
    },

    parseTitle(title = '') {
      return title
        .toString()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/ñ/g, 'n')
        .replace(/[^a-z0-9\s]/g, '');
    },

    async fetchArticlesByQuery(limit = null, isEnterMode = false) {
      try {
        this.searchResults = [];
        const defaultLimit = this.isHeaderMode
          ? RESULTS_LIMIT_HEADER
          : RESULTS_LIMIT_PAGE;
        const resultsLimit = limit !== null ? limit : defaultLimit;
        const { data } = await ArticlesAPI.searchArticles(
          this.portalSlug,
          this.localeCode,
          this.parseTitle(this.searchTerm),
          false,
          resultsLimit,
          isEnterMode
        );
        this.searchResults = data.payload;
        this.hasSearchError = false;
        this.summary =
          data.summary && data.summary.content ? data.summary.content : '';
        this.itemTotal = (this.searchResults || []).length;
        if (isEnterMode) {
          this.trackSearchResults();
        }
      } catch (_error) {
        this.hasSearchError = true;
      } finally {
        this.isLoading = false;
      }
    },

    trackSearchResults() {
      const payload = {
        event: 'view_search_results',
        searchQuery: this.searchTerm,
        resultsCount: this.itemTotal,
      };
      if (this.summary) {
        payload.aiSearchResponse =
          this.summary.length > AI_SUMMARY_MAX_CHARS
            ? this.summary.slice(0, AI_SUMMARY_MAX_CHARS)
            : this.summary;
      }
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(payload);
      if (window.ChatwootTracker) {
        window.ChatwootTracker.sendTrack(payload.event, payload);
      }
    },

    hideContent404() {
      const el = document.getElementById(DOM_IDS.content404);
      if (el) el.classList.add('hidden');
    },

    autoExtractQuery() {
      const urlParams = new URLSearchParams(window.location.search);
      const query = urlParams.get('query');
      if (query) {
        this.searchTerm = query;
        this.isAutoSearch = true;
        this.isEnter = true;
        this.hideContent404();
      }
      if (this.isSearchPage && !query) {
        this.isEnter = true;
      }
    },

    handleFeedback(feedbackType) {
      this.feedbackSubmitted = feedbackType;
      this.showThankYou = true;
      const payload = {
        event: 'pageDataClickAiFeedback',
        searchQuery: this.searchTerm,
        aiSearchFeedback: feedbackType === 'positive',
        aiSearchResponse:
          this.summary.length > AI_SUMMARY_MAX_CHARS
            ? this.summary.slice(0, AI_SUMMARY_MAX_CHARS)
            : this.summary,
      };
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(payload);
      if (window.ChatwootTracker) {
        window.ChatwootTracker.sendTrack(payload.event, payload);
      }
    },
  },
};
